import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { XClose, Minus, Plus, ChevronDown } from '@untitledui/icons'
import useRetailerApi from '@/useRetailerApi'
import useTranslation from '@/hooks/useTranslation'
import useCart from '@/hooks/useCart'
import { LANGUAGE_LOCALES } from '@/locales/languages'
import ResponsiveModal from '@/components/ResponsiveModal'
import Button from '@/components/Button'
import OptimizedImage from '@/components/OptimizedImage'

const STOCK_BADGE_CONFIG = {
    available: { bg: 'bg-green-500/10', text: 'text-green-500', key: 'dashboard.inStock' },
    low: { bg: 'bg-orange-400/10', text: 'text-orange-400', key: 'dashboard.stockLow' },
    unavailable: { bg: 'bg-red-400/10', text: 'text-red-400', key: 'dashboard.outOfStock' },
}

const stockConfig = (status) => STOCK_BADGE_CONFIG[status] ?? STOCK_BADGE_CONFIG.unavailable

function VariantStockBadge({ status, stockQuantity }) {
    const { t } = useTranslation()
    const config = stockConfig(status)
    return (
        <div className={`flex-1 h-[42px] ${config.bg} rounded-[8px] flex items-center justify-center`}>
            <span className={`text-[12px] uppercase ${config.text}`}>
                {t(config.key)}{stockQuantity != null ? ` (${stockQuantity})` : ''}
            </span>
        </div>
    )
}

function getMetaobjectField(metaobject, field, language) {
    return metaobject?.translations?.[language]?.[field] ?? metaobject?.[field]
}

function VisualOptionSelector({ option, selectedValue, onSelect, stockStatusFor }) {
    const { t, language } = useTranslation()
    return (
        <div className="flex flex-col gap-[8px]">
            <span className="text-[12px] uppercase text-dark-blue-20">
                {t(`option.${option.name}`) !== `option.${option.name}` ? t(`option.${option.name}`) : option.name}
            </span>
            <div className="flex flex-wrap gap-[4px]">
                {option.values.map((v) => {
                    const label = getMetaobjectField(v.metaobject, 'name', language) ?? v.value
                    const isUnavailable = stockStatusFor(v.value) === 'unavailable'
                    return (
                        <button
                            key={v.value}
                            onClick={() => onSelect(v.value)}
                            title={isUnavailable ? `${label} — ${t('dashboard.outOfStock')}` : label}
                            className={`w-[66.67px] h-[66.67px] bg-background-product rounded-[8px] flex items-center justify-center overflow-hidden cursor-pointer ${
                                selectedValue === v.value
                                    ? 'border border-dark-blue'
                                    : ''
                            }`}
                        >
                            <OptimizedImage
                                className={`object-contain w-[80px] h-[80px] ${isUnavailable ? 'opacity-25' : ''}`}
                                src={v.metaobject?.image_url}
                                alt={label}
                                sizes="80px"
                                widths={[80, 160]}
                            />
                        </button>
                    )
                })}
            </div>
            {selectedValue && (
                <span className="text-[14px] uppercase">
                    {getMetaobjectField(
                        option.values.find((v) => v.value === selectedValue)?.metaobject,
                        'name',
                        language,
                    ) ?? selectedValue}
                </span>
            )}
        </div>
    )
}

function DropdownOptionSelector({ option, selectedValue, onSelect, stockStatusFor }) {
    const { t, language } = useTranslation()
    const [open, setOpen] = useState(false)
    const triggerRef = useRef(null)
    const dropdownRef = useRef(null)
    const [dropdownStyle, setDropdownStyle] = useState({})

    const getLabel = (v) => getMetaobjectField(v.metaobject, 'name', language) ?? v.value

    useEffect(() => {
        if (!open) return
        const handleClickOutside = (e) => {
            if (
                triggerRef.current?.contains(e.target) ||
                dropdownRef.current?.contains(e.target)
            ) return
            setOpen(false)
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [open])

    useEffect(() => {
        if (open && triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect()
            setDropdownStyle({
                position: 'fixed',
                top: rect.bottom + 4,
                left: rect.left,
                width: rect.width,
            })
        }
    }, [open])

    return (
        <div className="flex flex-col gap-[8px]">
            <span className="text-[12px] uppercase text-dark-blue-20">
                {t(`option.${option.name}`) !== `option.${option.name}` ? t(`option.${option.name}`) : option.name}
            </span>
            <div ref={triggerRef}>
                <button
                    onClick={() => setOpen(!open)}
                    className="w-full h-[42px] border border-dark-blue-10 rounded-[8px] px-[12px] flex items-center justify-between gap-[8px] cursor-pointer"
                >
                    <span className="text-[14px] uppercase truncate">
                        {selectedValue
                            ? getLabel(option.values.find((v) => v.value === selectedValue) ?? { value: selectedValue })
                            : '—'}
                    </span>
                    <ChevronDown size={14} className="shrink-0" />
                </button>
            </div>

            {open && createPortal(
                <div
                    ref={dropdownRef}
                    style={dropdownStyle}
                    className="bg-white border border-dark-blue-10 rounded-[8px] z-50 overflow-hidden shadow-[0px_4px_12px_rgba(0,0,0,0.08)]"
                >
                    {option.values.map((v) => {
                        const isUnavailable = stockStatusFor(v.value) === 'unavailable'
                        return (
                            <button
                                key={v.value}
                                onClick={() => {
                                    onSelect(v.value)
                                    setOpen(false)
                                }}
                                className={`w-full h-[42px] px-[12px] flex items-center justify-between gap-[8px] text-[14px] uppercase text-left hover:bg-gray-50 cursor-pointer ${isUnavailable ? 'text-dark-blue-40' : ''}`}
                            >
                                <span className="truncate">{getLabel(v)}</span>
                                {isUnavailable && (
                                    <span className="text-[11px] whitespace-nowrap shrink-0">
                                        {t('dashboard.outOfStock')}
                                    </span>
                                )}
                            </button>
                        )
                    })}
                </div>,
                document.body,
            )}
        </div>
    )
}

function ProductImage({ src, alt }) {
    // The pane itself carries the ratio of the product cards, so the modal never
    // resizes with the packshot
    return (
        <div className="h-full aspect-442/589 shrink-0 flex items-center justify-center p-[24px] lg:p-[60px]">
            <OptimizedImage
                className="w-full h-full object-contain"
                src={src}
                alt={alt}
                sizes="330px"
                widths={[360, 720]}
            />
        </div>
    )
}

function ProductOptions({ product, price, originalPrice, sku, options, effectiveSelections, handleOptionSelect, stockStatusForOption, isAdmin }) {
    const { t, language } = useTranslation()
    const fmt = (v) => v.toLocaleString(LANGUAGE_LOCALES[language], { style: 'currency', currency: 'EUR' })
    return (
        <>
            <span className="text-[32px] uppercase leading-none">
                {product.title}
            </span>

            {(isAdmin || sku) && (
                <div className="flex flex-col gap-[4px]">
                    {isAdmin && (
                        <>
                            <span className="text-[24px] leading-none">
                                {fmt(price)}
                            </span>
                            {originalPrice !== price && (
                                <span className="text-[14px] leading-none text-dark-blue-40">
                                    {t('dashboard.referencePrice')} : {fmt(originalPrice)}
                                </span>
                            )}
                        </>
                    )}
                    {sku && (
                        <span className="text-[14px] leading-none text-dark-blue-40">
                            SKU : {sku}
                        </span>
                    )}
                </div>
            )}

            {product.variants.length > 1 && options.map((option) => {
                const hasImages = option.values.some((v) => v.metaobject?.image_url)

                const selectorProps = {
                    option,
                    selectedValue: effectiveSelections[option.name],
                    onSelect: (value) => handleOptionSelect(option.name, value),
                    stockStatusFor: (value) => stockStatusForOption(option.name, value),
                }

                return hasImages ? (
                    <VisualOptionSelector key={option.name} {...selectorProps} />
                ) : (
                    <DropdownOptionSelector key={option.name} {...selectorProps} />
                )
            })}
        </>
    )
}

function ProductFooter({ variant, quantity, setQuantity, t, canOrder, onAddToCart, inCart, isDirty, canIncrement, justAdded }) {
    return (
        <div className="flex flex-col gap-[12px] pt-[24px]">
            <div className="flex gap-[4px]">
                {canOrder && (
                    <div className="flex-1 h-[42px] border border-dark-blue-10 rounded-[8px] px-[4px] flex items-center justify-between select-none">
                        <button
                            disabled={quantity <= 0}
                            onClick={() => setQuantity(Math.max(0, quantity - 1))}
                            className={`w-[34px] h-[34px] flex items-center justify-center rounded-[6px] transition-colors ${quantity > 0 ? 'cursor-pointer hover:bg-dark-blue/4' : 'opacity-20 cursor-default'}`}
                        >
                            <Minus size={14} />
                        </button>
                        <span className="text-[14px] font-mono tabular-nums">
                            {String(quantity).padStart(2, '0')}
                        </span>
                        <button
                            disabled={!canIncrement}
                            onClick={() => setQuantity(quantity + 1)}
                            className={`w-[34px] h-[34px] flex items-center justify-center rounded-[6px] transition-colors ${canIncrement ? 'cursor-pointer hover:bg-dark-blue/4' : 'opacity-20 cursor-default'}`}
                        >
                            <Plus size={14} />
                        </button>
                    </div>
                )}

                <VariantStockBadge status={variant?.stock_status} stockQuantity={variant?.stock_quantity} />
            </div>

            {canOrder && (
                <Button fullWidth onClick={onAddToCart} disabled={!isDirty || (quantity < 1 && !inCart)}>
                    {justAdded ? t('dashboard.addedToCart') : isDirty ? (inCart ? t('dashboard.updateCart') : t('dashboard.addToCart')) : (inCart ? t('dashboard.addedToCart') : t('dashboard.addToCart'))}
                </Button>
            )}
        </div>
    )
}

export default function ProductModal({ product, isOpen, onClose, initialVariantId }) {
    const { retailer } = useRetailerApi()
    const { t } = useTranslation()
    const { cartItems, addToCart, updateQuantity: updateCartQuantity, removeFromCart, canAddToProduct } = useCart()
    const isAdmin = retailer.access_level === 'admin'
    const discount = 1 - (retailer.default_markup_rate ?? 0)
    const canOrder = isAdmin

    const lastProductRef = useRef(product)
    if (product) lastProductRef.current = product
    const displayProduct = product ?? lastProductRef.current

    const [selectedOptions, setSelectedOptions] = useState({})
    const [quantity, setQuantity] = useState(0)
    const [justAdded, setJustAdded] = useState(false)
    const justAddedTimer = useRef(null)

    const options = displayProduct?.options ?? []

    // Pre-select the options of a specific variant (from cart) or, by default,
    // of the first variant actually in stock
    useEffect(() => {
        if (!isOpen || !displayProduct) return

        const variants = displayProduct.variants ?? []
        const targetVariant =
            (initialVariantId && variants.find((v) => v.id === initialVariantId)) ||
            variants.find((v) => v.stock_status === 'available') ||
            variants.find((v) => v.stock_status === 'low')

        if (!targetVariant) {
            setSelectedOptions({})
            return
        }

        setSelectedOptions(
            Object.fromEntries(targetVariant.selected_options.map((so) => [so.name, so.value]))
        )
    }, [isOpen, displayProduct?.id, initialVariantId])

    const effectiveSelections = {}
    for (const option of options) {
        effectiveSelections[option.name] = selectedOptions[option.name] ?? option.values[0]?.value
    }

    const variant = displayProduct?.variants?.length
        ? displayProduct.variants.find((v) =>
            v.selected_options.every(
                (so) => effectiveSelections[so.name] === so.value
            )
        ) ?? displayProduct.variants[0]
        : null

    // Sync quantity with what's in the cart for the current variant
    const cartItem = cartItems.find((i) => i.variantId === variant?.id)

    const prevVariantIdRef = useRef(variant?.id)
    const prevIsOpenRef = useRef(isOpen)
    if (variant?.id !== prevVariantIdRef.current || isOpen !== prevIsOpenRef.current) {
        prevVariantIdRef.current = variant?.id
        prevIsOpenRef.current = isOpen
        if (isOpen) {
            setQuantity(cartItem?.quantity ?? 0)
            setJustAdded(false)
            clearTimeout(justAddedTimer.current)
        }
    }

    useEffect(() => {
        return () => clearTimeout(justAddedTimer.current)
    }, [])

    const originalPrice = variant ? variant.price / 100 : 0
    const price = variant ? (variant.price * discount) / 100 : 0

    const handleOptionSelect = (optionName, value) => {
        setSelectedOptions((prev) => ({ ...prev, [optionName]: value }))
    }

    // Stock status of the variant this option value would lead to, keeping the
    // other options as currently selected
    const stockStatusForOption = (optionName, value) => {
        const target = { ...effectiveSelections, [optionName]: value }
        const match = displayProduct?.variants?.find((v) =>
            v.selected_options.every((so) => target[so.name] === so.value)
        )
        return match?.stock_status ?? 'unavailable'
    }

    const handleAddToCart = () => {
        if (!variant || !displayProduct) return
        if (cartItem && quantity === 0) {
            removeFromCart(variant.id)
        } else if (cartItem) {
            updateCartQuantity(variant.id, quantity)
        } else if (quantity >= 1) {
            addToCart({
                variantId: variant.id,
                productTitle: displayProduct.title,
                variantTitle: variant.title,
                imageUrl: variant.image_url ?? displayProduct.image_url,
                price,
                quantity,
                sku: variant.sku,
                options: variant.selected_options,
            })
        }
        clearTimeout(justAddedTimer.current)
        setJustAdded(true)
        justAddedTimer.current = setTimeout(() => setJustAdded(false), 1500)
    }

    // Check if incrementing quantity would exceed the per-product limit or variant stock
    const cartQtyForVariant = cartItem?.quantity ?? 0
    const withinProductLimit = displayProduct ? canAddToProduct(displayProduct.title, quantity - cartQtyForVariant + 1) : true
    const isUnavailable = variant?.stock_status === 'unavailable'
    const withinStockLimit = variant?.stock_status === 'low' && variant?.stock_quantity != null ? quantity < variant.stock_quantity : true
    const canIncrement = !isUnavailable && withinProductLimit && withinStockLimit

    const imageUrl = variant?.image_url ?? displayProduct?.image_url
    const optionsProps = { product: displayProduct, price, originalPrice, sku: variant?.sku, options, effectiveSelections, handleOptionSelect, stockStatusForOption, isAdmin }
    const isDirty = quantity !== cartQtyForVariant
    const footerProps = { variant, quantity, setQuantity, t, canOrder: canOrder && !isUnavailable, onAddToCart: handleAddToCart, inCart: !!cartItem, isDirty, canIncrement, justAdded }

    return (
        <ResponsiveModal
            isOpen={isOpen}
            onClose={onClose}
            desktopClassName="w-[1000px] max-w-[95vw] h-[600px] max-h-[90vh] rounded-[8px] shadow-[0px_0px_24px_12px_rgba(0,0,0,0.04)] flex"
        >
            <div className="flex-1 overflow-y-auto px-[24px] pb-[8px] flex flex-col gap-[24px] md:hidden">
                {displayProduct && <ProductOptions {...optionsProps} />}
            </div>
            <div className="px-[24px] pb-[24px] md:hidden">
                <ProductFooter {...footerProps} />
            </div>

            <div className="hidden md:flex flex-1">
                <ProductImage src={imageUrl} alt={displayProduct?.title} />

                <button
                    onClick={onClose}
                    className="absolute top-[16px] right-[16px] cursor-pointer p-[4px] z-10"
                >
                    <XClose size={20} />
                </button>

                <div className="flex-1 min-w-0 min-h-0 flex flex-col border-l border-dark-blue-10">
                    <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-[24px] px-[24px] lg:px-[40px] pt-[24px] lg:pt-[40px]">
                        <ProductOptions {...optionsProps} />
                    </div>
                    <div className="shrink-0 px-[24px] lg:px-[40px] pb-[24px] lg:pb-[40px]">
                        <ProductFooter {...footerProps} />
                    </div>
                </div>
            </div>
        </ResponsiveModal>
    )
}
